import { createSlice } from '@reduxjs/toolkit';
import {
    SLICE_NAME,
    appInstallationCallback,
    createProject,
    getGithubAppUrl,
    getUsers,
    updateProject,
    getSkills
} from './service';

export interface ProjectOnboardingState {
    projectDetails: {
        loading: boolean;
        success: boolean;
        details: {
            projectId: string | null;
            projectName: string | null;
            description: string | null;
            githubConnected: boolean;
            githubAppUrl: string | null;
        };
    };
    userList: {
        loading: boolean;
        success: boolean;
        users: {
            name: string;
            email: string;
        }[];
    };
    updateProject: {
        loading: boolean;
        success: boolean;
    };
    skills: {
        id: number;
        name: string;
    }[];
}

const initialState: ProjectOnboardingState = {
    projectDetails: {
        loading: false,
        success: false,
        details: {
            projectId: null,
            projectName: null,
            description: null,
            githubConnected: false,
            githubAppUrl: null
        }
    },
    userList: {
        loading: false,
        success: false,
        users: []
    },
    updateProject: {
        loading: false,
        success: false
    },
    skills: []
};

const projectOnboarding = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
        clearUserState: () => initialState,
        updateProjectDetails: (state, action) => {
            state.projectDetails.details = {
                ...state.projectDetails.details,
                ...action.payload
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(appInstallationCallback.pending, (state) => {
                state.projectDetails.loading = true;
                state.projectDetails.success = false;
                state.projectDetails.details.projectId = null;
            })
            .addCase(appInstallationCallback.fulfilled, (state, action) => {
                state.projectDetails.loading = false;
                state.projectDetails.success = true;
                state.projectDetails.details.projectId =
                    action.payload.projectId;
                state.projectDetails.details.githubConnected =
                    action.payload.githubConnected;
            })
            .addCase(appInstallationCallback.rejected, (state) => {
                state.projectDetails.loading = false;
                state.projectDetails.success = false;
                state.projectDetails.details.projectId = null;
                state.projectDetails.details.githubConnected = false;
            })

            .addCase(createProject.pending, (state) => {
                state.projectDetails.loading = true;
                state.projectDetails.success = false;
                state.projectDetails.details.projectId = null;
                state.projectDetails.details.projectName = null;
            })
            .addCase(createProject.fulfilled, (state, action) => {
                state.projectDetails.loading = false;
                state.projectDetails.success = true;
                state.projectDetails.details.projectId =
                    action.payload.projectId;
                state.projectDetails.details.projectName =
                    action.payload.projectName;
            })
            .addCase(createProject.rejected, (state) => {
                state.projectDetails.loading = false;
                state.projectDetails.success = false;
                state.projectDetails.details.projectId = null;
                state.projectDetails.details.projectName = null;
            })

            .addCase(getGithubAppUrl.pending, (state) => {
                state.projectDetails.loading = true;
                state.projectDetails.success = false;
                state.projectDetails.details.githubAppUrl = null;
            })
            .addCase(getGithubAppUrl.fulfilled, (state, action) => {
                state.projectDetails.loading = false;
                state.projectDetails.success = true;
                state.projectDetails.details.githubAppUrl =
                    action.payload.githubAppUrl;
            })
            .addCase(getGithubAppUrl.rejected, (state) => {
                state.projectDetails.loading = false;
                state.projectDetails.success = false;
                state.projectDetails.details.githubAppUrl = null;
            })

            .addCase(getUsers.pending, (state) => {
                state.userList.loading = true;
                state.userList.success = false;
                state.userList.users = [];
            })
            .addCase(getUsers.fulfilled, (state, action) => {
                state.userList.loading = false;
                state.userList.success = true;
                state.userList.users = action.payload;
            })
            .addCase(getUsers.rejected, (state) => {
                state.userList.loading = false;
                state.userList.success = false;
                state.userList.users = [];
            })

            .addCase(updateProject.pending, (state) => {
                state.updateProject.loading = true;
                state.updateProject.success = false;
            })
            .addCase(updateProject.fulfilled, (state) => {
                state.updateProject.loading = false;
                state.updateProject.success = true;
            })
            .addCase(updateProject.rejected, (state) => {
                state.updateProject.loading = false;
                state.updateProject.success = false;
            })

            .addCase(getSkills.pending, (state) => {
                state.skills = [];
            })
            .addCase(getSkills.fulfilled, (state, action) => {
                state.skills = action.payload;
            })
            .addCase(getSkills.rejected, (state) => {
                state.skills = [];
            });
    }
});

export const { updateProjectDetails } = projectOnboarding.actions;

export default projectOnboarding.reducer;
